import React from "react";
import HistoryDataSidebar, {
  HistoryDataSidebarSummaryItem,
} from "components/shared/history-data-sidebar";
import { useDriverHistory } from "../hooks/useDriverHistory";
import { useAppTranslation } from "locale/useAppTranslation";
import { DriverServiceV1GetDriverDetailApiResponse as GetDriverDetailApiResponse } from "_services/modules/transport-resource/driver";
import { historyDateTimeFormatter } from "_helper/timeFormatter";
import { displayUserName } from "_helper/actor";

interface DriverHistorySidebarProps {
  open: boolean;
  onClose: () => void;
  driverId?: string;
  detail?: GetDriverDetailApiResponse;
}

const DriverHistorySidebar: React.FC<DriverHistorySidebarProps> = ({
  open,
  onClose,
  driverId,
  detail,
}) => {
  const { t } = useAppTranslation("driver");
  const { t: tHistory } = useAppTranslation("historyDataSidebar");

  const {
    items,
    isLoading,
    isLoadingMore,
    hasMore,
    loadMore,
    searchValue,
    setSearchValue,
    fromDateValue,
    toDateValue,
    onDateChange,
  } = useDriverHistory({ driverId, isOpen: open });

  const summaryItems: HistoryDataSidebarSummaryItem[] = [];

  if (detail?.created_by) {
    const creatorName = displayUserName(detail.created_by.name);
    const dateStr = detail.created_by.timestamp
      ? historyDateTimeFormatter(detail.created_by.timestamp)
      : "—";
    summaryItems.push({
      id: "created-by",
      label: tHistory("created-by") || "Created by",
      value: `${creatorName}, ${dateStr}`,
    });
  }

  if (detail?.updated_by || detail?.created_by) {
    const updaterName = displayUserName(detail?.updated_by?.name || detail?.created_by?.name);
    const dateStr = (detail?.updated_by?.timestamp || detail?.created_by?.timestamp)
      ? historyDateTimeFormatter(detail?.updated_by?.timestamp || detail?.created_by?.timestamp)
      : "—";
    summaryItems.push({
      id: "updated-by",
      label: tHistory("last-update-by") || "Last Update by",
      value: `${updaterName}, ${dateStr}`,
    });
  }

  return (
    <HistoryDataSidebar
      open={open}
      onClose={onClose}
      items={items}
      summaryItems={summaryItems}
      title={t("history-title") || "History Data"}
      subtitle={t("history-subtitle") || "Driver History Data"}
      emptyTitle={
        isLoading ? "Loading history..." : t("history-empty-title") || undefined
      }
      emptyDescription={
        t("history-empty-description") ||
        "There is no history data for this driver."
      }
      searchValue={searchValue}
      onSearchChange={setSearchValue}
      fromDateValue={fromDateValue}
      toDateValue={toDateValue}
      onDateChange={onDateChange}
      hasMore={hasMore}
      isLoadingMore={isLoadingMore}
      onLoadMore={loadMore}
    />
  );
};

export default DriverHistorySidebar;
